// utils/blogApi.js
// Final, bulletproof version — works everywhere

const resolveBaseUrl = () => {
  // 1. Vercel preview / custom domain
  if (process.env.NEXT_PUBLIC_BLOG_API_URL?.trim()) {
    return process.env.NEXT_PUBLIC_BLOG_API_URL.trim().replace(/\/$/, '');
  }

  // 2. General API override
  if (process.env.NEXT_PUBLIC_API_URL?.trim()) {
    return process.env.NEXT_PUBLIC_API_URL.trim().replace(/\/$/, '');
  }

  // 3. Client-side: use relative URLs (best practice for Next.js)
  if (typeof window !== 'undefined') {
    return ''; // → /api/blog becomes same-origin
  }

  // 4. Server-side fallback (SSR, API routes, etc.)
  return 'http://localhost:3001';
};

const API_BASE_URL = resolveBaseUrl();

/**
 * Transform backend post → frontend format
 */
export const transformBlogData = (post) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Just now';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const imageUrl = post.featured_image?.trim() || '';
  const isLocalUpload = imageUrl.startsWith('/uploads/');
  const fullImageUrl = isLocalUpload ? `${API_BASE_URL}${imageUrl}` : imageUrl;

  const categories = post.category ? [post.category.trim()] : [];
  const tags = post.tags
    ? post.tags.split(',').map(t => t.trim()).filter(Boolean)
    : [];

  const body = post.content
    ? post.content.includes('<') && post.content.includes('>')
      ? [post.content]
      : [`<p>${post.content.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>')}</p>`]
    : [];

  const publishedDate = post.published_at || post.created_at;

  return {
    id: post.id,
    title: post.title || 'Untitled Post',
    slug: post.slug || '',
    excerpt: post.excerpt || '',
    content: post.content || '',
    image: fullImageUrl,
    largeImage: fullImageUrl,
    image_alt_text: post.image_alt_text?.trim() || '',
    author: post.author ? [post.author.trim()] : ['Admin'],
    authorName: post.author?.trim() || 'Admin',
    date: formatDate(publishedDate),
    categories,
    tags,
    status: post.status || 'draft',
    meta_title: post.meta_title || post.title || 'Untitled',
    meta_description: post.meta_description || post.excerpt || '',
    view: '70 views' // Replace with real count later
  };
};

/**
 * Fetch blog posts
 */
export const fetchBlogPosts = async ({
  page = 1,
  limit = 12,
  status = 'published',
  category,
  tag
} = {}) => {
  try {
    let url = `${API_BASE_URL}/api/blog?page=${page}&limit=${limit}`;

    if (status && status !== 'published') {
      url += `&status=${status}`;
    }
    if (category) {
      url += `&category=${encodeURIComponent(category)}`;
    }
    if (tag) {
      url += `&tag=${encodeURIComponent(tag)}`;
    }

    const res = await fetch(url, {
      credentials: 'include',
      cache: 'no-store' // Fresh data every time (good for admin)
    });

    if (!res.ok) {
      console.error(`[Blog API] ${res.status} ${res.statusText}`, await res.text());
      return { posts: [], pagination: null };
    }

    const json = await res.json();

    if (!json.success || !Array.isArray(json.data)) {
      console.warn('[Blog API] Invalid response:', json);
      return { posts: [], pagination: null };
    }

    const posts = json.data.map(transformBlogData);

    return {
      posts,
      pagination: json.pagination || {
        page: Number(page),
        limit: Number(limit),
        total: posts.length,
        pages: Math.ceil(posts.length / limit)
      }
    };
  } catch (err) {
    console.error('[Blog API] fetchBlogPosts failed:', err);
    return { posts: [], pagination: null };
  }
};

/**
 * Fetch single post by ID or slug
 */
export const fetchBlogPost = async (identifier, { useSlug = false, includeDrafts = false } = {}) => {
  try {
    const status = includeDrafts ? 'all' : 'published';
    const url = useSlug
      ? `${API_BASE_URL}/api/blog/slug/${identifier}`
      : `${API_BASE_URL}/api/blog/${identifier}?status=${status}`;

    const res = await fetch(url, { cache: 'no-store' });

    if (!res.ok) {
      console.error(`[Blog API] Failed to fetch post: ${res.status}`);
      return null;
    }

    const json = await res.json();

    if (json.success && json.data) {
      return transformBlogData(json.data);
    }

    return null;
  } catch (err) {
    console.error('[Blog API] fetchBlogPost failed:', err);
    return null;
  }
};

/**
 * Fetch categories
 */
export const fetchCategories = async () => {
  try {
    const res = await fetch(`${API_BASE_URL}/api/categories`, { cache: 'no-store' });
    const json = await res.json();
    return json.success && Array.isArray(json.data) ? json.data : [];
  } catch (err) {
    console.error('[Blog API] fetchCategories failed:', err);
    return [];
  }
};

/**
 * Fetch tags with counts
 */
export const fetchTags = async () => {
  try {
    const res = await fetch(`${API_BASE_URL}/api/tags`, { cache: 'no-store' });
    const json = await res.json();
    return json.success && Array.isArray(json.data) ? json.data : [];
  } catch (err) {
    console.error('[Blog API] fetchTags failed:', err);
    return [];
  }
};

export default {
  fetchBlogPosts,
  fetchBlogPost,
  fetchCategories,
  fetchTags,
  transformBlogData
};